import { useCallback, useRef, useState } from 'react'
import axios from 'axios'
import { API_BASE_URL } from '../constants/system'

export interface PendingAttachment {
  id: string
  file: File
  previewUrl?: string
  uploadState: 'pending' | 'uploading' | 'uploaded' | 'error'
  artifactId?: string
  error?: string
}

const MAX_FILE_SIZE = 100 * 1024 * 1024 // 100MB

export default function useFileAttachments() {
  const [attachments, setAttachments] = useState<PendingAttachment[]>([])
  const idCounterRef = useRef(0)

  const addFiles = useCallback((files: FileList | File[]) => {
    const newAttachments: PendingAttachment[] = []
    for (const file of Array.from(files)) {
      if (file.size > MAX_FILE_SIZE) continue

      const id = `attachment-${++idCounterRef.current}`
      const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined
      newAttachments.push({ id, file, previewUrl, uploadState: 'pending' })
    }
    if (newAttachments.length > 0) {
      setAttachments(prev => [...prev, ...newAttachments])
    }
  }, [])

  const removeFile = useCallback((id: string) => {
    setAttachments(prev => {
      const item = prev.find(a => a.id === id)
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl)
      return prev.filter(a => a.id !== id)
    })
  }, [])

  const uploadAll = useCallback(async (sessionId: string): Promise<string[]> => {
    const pending = attachments.filter(a => a.uploadState === 'pending')
    if (pending.length === 0) return attachments.filter(a => a.artifactId).map(a => a.artifactId!)

    // Mark all pending as uploading
    setAttachments(prev => prev.map(a =>
      a.uploadState === 'pending' ? { ...a, uploadState: 'uploading' as const } : a
    ))

    const artifactIds: string[] = []

    for (const attachment of pending) {
      try {
        const formData = new FormData()
        formData.append('file', attachment.file, attachment.file.name)
        const resp = await axios.post<{ id: string }>(
          `${API_BASE_URL}/agent/session/${sessionId}/upload`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )
        const artifactId = resp.data.id
        artifactIds.push(artifactId)
        setAttachments(prev => prev.map(a =>
          a.id === attachment.id ? { ...a, uploadState: 'uploaded' as const, artifactId } : a
        ))
      } catch (err) {
        setAttachments(prev => prev.map(a =>
          a.id === attachment.id ? { ...a, uploadState: 'error' as const, error: 'Upload failed' } : a
        ))
      }
    }

    // Also include previously uploaded IDs
    const existingIds = attachments.filter(a => a.artifactId).map(a => a.artifactId!)
    return [...existingIds, ...artifactIds]
  }, [attachments])

  const handlePaste = useCallback((e: ClipboardEvent) => {
    const items = e.clipboardData?.items
    if (!items) return
    const files: File[] = []
    for (const item of Array.from(items)) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile()
        if (file) files.push(file)
      }
    }
    if (files.length > 0) {
      addFiles(files)
    }
  }, [addFiles])

  const clear = useCallback(() => {
    setAttachments(prev => {
      for (const a of prev) {
        if (a.previewUrl) URL.revokeObjectURL(a.previewUrl)
      }
      return []
    })
  }, [])

  return { attachments, addFiles, removeFile, uploadAll, handlePaste, clear }
}
